/**
 * Stabiler Device Fingerprint fuer Hotel TV Registrierung.
 *
 * Basis: serialNumber + mac + model — nur hardware-gebundene Werte.
 * Format: "<serialNumber>|<mac>|<model>" → SHA-256 (64 Hex-Zeichen)
 *
 * Eigenschaften:
 *   - Deterministisch: gleiche Hardware → immer gleicher Hash
 *   - Firmware-unabhaengig: kein version/timestamp/random
 *   - Einheitlich: alle Plattformen (Tizen, LG, Browser) → gleiche Struktur
 *   - Vollstaendig oder gar nicht: fehlt einer der drei Werte, kommt null
 *     zurueck statt eines Hashes ueber eine Luecke
 *
 * Es gibt hier BEWUSST keinen Ersatz-Fingerprint aus UserAgent/Aufloesung mehr.
 * Ein solcher Wert kann einen DB-Eintrag, der aus serial|mac|model entstanden
 * ist, prinzipiell nie treffen - er haette beim Nachschlagen nichts gefunden und
 * beim Anlegen einen zweiten Eintrag fuer dasselbe Geraet erzeugt.
 */

import Device from './Device';

/**
 * Platzhalter, den Tizen.getMac und LG.getMac liefern, wenn die MAC nicht
 * lesbar ist. Er ist der gefaehrlichste der drei Ausfaelle: er ist NICHT leer,
 * sieht aus wie eine MAC und ist stabil - der Fingerprint daraus waere also
 * reproduzierbar falsch statt erkennbar kaputt.
 */
const MAC_PLACEHOLDER = '00:00:00:00:00';

/** Eine vollstaendige MAC hat sechs Oktette. Der Platzhalter hat fuenf. */
const MAC_PATTERN = /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i;

/**
 * Die Geraetedaten koennen beim Start voruebergehend nicht lesbar sein (die
 * Helfer liefern dann '' bzw. den MAC-Platzhalter, ohne zu werfen). Deshalb
 * wird kurz nachgefasst, bevor aufgegeben wird.
 */
const IDENTITY_ATTEMPTS = 3;
const IDENTITY_RETRY_MS = 400;

function clean(value) {
  return String(value === null || value === undefined ? '' : value).trim();
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class DeviceFingerprint {
  constructor() {
    this.fingerprintLength = 64;
  }

  /**
   * Welche der drei Pflichtangaben fehlt oder unbrauchbar ist.
   * Leeres Array = alles vorhanden.
   */
  missingParts({ serialNumber, mac, model }) {
    const missing = [];
    if (!serialNumber) missing.push('serialNumber');
    if (!model) missing.push('model');
    if (!mac || mac.toLowerCase() === MAC_PLACEHOLDER || !MAC_PATTERN.test(mac)) {
      missing.push('mac');
    }
    return missing;
  }

  /**
   * serialNumber, mac und model lesen - mit kurzen Wiederholungen, solange
   * etwas fehlt. Rueckgabe: { identity, missing }.
   */
  async readIdentity() {
    let last = { identity: { serialNumber: '', mac: '', model: '' }, missing: ['serialNumber', 'mac', 'model'] };
    for (let attempt = 1; attempt <= IDENTITY_ATTEMPTS; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const [serialNumber, mac, model] = await Promise.all([
        Device.serialNumber,
        Device.mac,
        Device.model,
      ]);
      const identity = { serialNumber: clean(serialNumber), mac: clean(mac), model: clean(model) };
      const missing = this.missingParts(identity);
      last = { identity, missing };
      if (!missing.length) return last;
      if (attempt < IDENTITY_ATTEMPTS) {
        console.log(
          '[Fingerprint] unvollstaendig (' + missing.join(', ') + ') - Versuch ' +
            attempt + ' von ' + IDENTITY_ATTEMPTS
        );
        // eslint-disable-next-line no-await-in-loop
        await wait(IDENTITY_RETRY_MS);
      }
    }
    return last;
  }

  /**
   * Gibt den SHA-256 Hash von "serialNumber|mac|model" zurueck (64 Hex-Zeichen)
   * - oder null, wenn einer der drei Werte fehlt bzw. unbrauchbar ist.
   * Die Hash-Funktion selbst faellt auf simpleHash zurueck, wenn crypto.subtle
   * fehlt (WebOS 3.5, Orsay); das Ergebnis bleibt deterministisch.
   */
  async generateFingerprint() {
    let read;
    try {
      read = await this.readIdentity();
    } catch (error) {
      console.warn('[Fingerprint] Geraetedaten nicht lesbar:', error);
      return null;
    }

    // ALLE DREI oder gar nichts.
    //
    // Fehlt einer, entstuende hier ein Hash ueber eine Luecke ("SERIAL||MODEL")
    // - formal ein einwandfreier 64-Hex-Wert, der aber den DB-Eintrag nie
    // trifft. Schlimmer noch: er wuerde gespeichert und von da an bevorzugt,
    // ein einmaliger Lesefehler haette den Fingerprint also dauerhaft
    // verdorben, und die Registrierung legte einen Doppeleintrag an. Deshalb
    // lieber null - der Aufrufer entscheidet dann, was er tut.
    if (read.missing.length) {
      console.warn(
        '[Fingerprint] nicht berechenbar - fehlt oder ungueltig: ' + read.missing.join(', ')
      );
      return null;
    }

    // Einheitliches Format: pipe-separiert, Leerzeichen getrimmt, Grossbuchstaben.
    // Reihenfolge serial|mac|model ist FESTGELEGT - jede Aenderung wuerde alle
    // bereits in der DB stehenden Fingerprints entwerten.
    const raw = [
      read.identity.serialNumber.toUpperCase(),
      read.identity.mac.toUpperCase(),
      read.identity.model.toUpperCase(),
    ].join('|');

    console.log('[Fingerprint] raw:', raw);
    return this.hash(raw);
  }

  /**
   * SHA-256 Hash → 64 Hex-Zeichen.
   * Fallback auf simpleHash fuer TVs ohne crypto.subtle.
   */
  async hash(input) {
    try {
      if (window.crypto && window.crypto.subtle) {
        const encoder = new TextEncoder();
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(input));
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');  // SHA-256 = genau 64 Hex-Zeichen
      }
      return this.simpleHash(input);
    } catch (error) {
      return this.simpleHash(input);
    }
  }

  /**
   * Einfacher deterministischer Hash als Fallback (WebOS 3.5, Orsay).
   * Gibt 64 Hex-Zeichen zurueck.
   */
  simpleHash(str) {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const hex = (4294967296 + h1).toString(16).slice(-8) + (4294967296 + h2).toString(16).slice(-8);
    // Auf 64 Zeichen strecken: Hash + Hash des Strings-Laenge
    return (hex + hex + str.length.toString(16).padStart(16, '0') + hex).substring(0, this.fingerprintLength);
  }

}

export default DeviceFingerprint;



// WEBPACK FOOTER //
// ./src/helpers/fingerprint.js